/*
 * DD AdminUI
 * Copyright (C) Fraunhofer IESE 2019 - 2025 Stefan Schweitzer, Shyalan Ramesh, Rafael Aranda López King,
 * Florian Wege, Johannes Schneider, Kazi Rezoanur Rahman, Christoph Müller, Pedram Jokar,
 * Mher Ter-Tovmasyan, Ben Burkhard, Matthias Gerbershagen, Anna Kleiner, Muhammad Tariq,
 * Adeline Silva Schäfer
 *
 * SPDX-License-Identifier: Apache-2.0
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React, { Component } from 'react';
import { Grid } from '@mui/material';
import {
	ClientUserGeneratedContentFlagGroup,
	ClientUserGeneratedContentFlagStatusRecord
} from '@DigitaleDoerfer/digitale-doerfer-api';
import FlaggedContentStatusHistoryView from './shared/FlaggedContentStatusHistory.view';
import { WithStyles, withStyles } from '@mui/styles';
import FlaggedContentDetailsReasonView from './shared/FlaggedContentDetailsReason.view';
import FlaggedContentDetailsStatusView from './shared/FlaggedContentDetailsStatus.view';
import { commonStyles, FlagSummary } from './shared/FlaggedContentDetailsCommons';
import { ReadOnlyTextFieldView } from '../../../shared/views/ReadOnlyTextField.view';
import { flaggedContentViewService } from '../../../ServiceFactory';
import { FlaggedType } from '../store/FlaggedContent.state';

interface Props extends WithStyles<typeof commonStyles> {
	flagSummary: FlagSummary;
	flaggedEntity: ClientUserGeneratedContentFlagGroup;
	statusRecords: ClientUserGeneratedContentFlagStatusRecord[];
}
class FlaggedGroupView extends Component<Props> {
	renderFlaggedEntityInformation(): JSX.Element {
		const {
			flagSummary,
			flaggedEntity: { flagGroup }
		} = this.props;
		const flaggedTextTitle = `${flaggedContentViewService().getEntityTypeDisplayName(
			FlaggedType.GROUP
		)} in ${flaggedContentViewService().renderInfo(flagSummary.tenantName)}`;

		return (
			<Grid container spacing={3}>
				<Grid item container spacing={3} xs={12}>
					<Grid item xs={12} sm={10}>
						<ReadOnlyTextFieldView
							id="name"
							name="name"
							label={flaggedTextTitle}
							multiline
							maxRows={5}
							value={flaggedContentViewService().renderInfo(flagGroup?.name)}
							InputProps={{ readOnly: true }}
							InputLabelProps={{
								shrink: true
							}}
							fullWidth
						/>
					</Grid>
					<Grid item xs={12} sm={2} container>
						<ReadOnlyTextFieldView
							id="shortName"
							name="shortName"
							label="Kürzel"
							multiline
							maxRows={5}
							value={flaggedContentViewService().renderInfo(flagGroup?.shortName)}
							InputProps={{ readOnly: true }}
							InputLabelProps={{
								shrink: true
							}}
							fullWidth
						/>
					</Grid>
					<Grid item xs={12} container>
						<ReadOnlyTextFieldView
							id="description"
							name="description"
							label="Beschreibung"
							multiline
							maxRows={10}
							value={flaggedContentViewService().renderInfo(flagGroup?.description)}
							InputProps={{ readOnly: true }}
							InputLabelProps={{
								shrink: true
							}}
							fullWidth
						/>
					</Grid>
				</Grid>
				<FlaggedContentDetailsReasonView reason={flagSummary.reason} />
				<FlaggedContentDetailsStatusView status={flagSummary.status} lastStatusUpdate={flagSummary.lastStatusUpdate} />
			</Grid>
		);
	}

	render(): JSX.Element {
		const { flagSummary, statusRecords } = this.props;
		return (
			<>
				{this.renderFlaggedEntityInformation()}
				<FlaggedContentStatusHistoryView flagSummary={flagSummary} statusRecords={statusRecords} />
			</>
		);
	}
}

export default withStyles(commonStyles)(FlaggedGroupView);
